import { supabase } from '@/lib/supabase'
import type { JobberRequestsQuery, JobberRequestsResult, JobberCityStatusBreakdown } from '../types'

export async function fetchJobberRequests({
  filters,
  pagination
}: JobberRequestsQuery): Promise<JobberRequestsResult> {
  try {
    console.log('Fetching Jobber requests with filters:', filters, 'pagination:', pagination)
    
    // Base query for count
    let countQuery = supabase
      .from('requests')
      .select('*', { count: 'exact', head: true })
    
    // Base query for data
    let query = supabase
      .from('requests')
      .select(`
        id,
        jobber_request_id,
        client_name,
        client_email,
        client_phone,
        request_title,
        franchise,
        status,
        jobber_created_at,
        service_city,
        service_province,
        service_zip,
        source,
        service_street
      `)
      .order('jobber_created_at', { ascending: false })
    
    // Apply filters to both queries
    if (filters.franchise && filters.franchise !== 'all') {
      countQuery = countQuery.eq('franchise', filters.franchise)
      query = query.eq('franchise', filters.franchise)
    }
    
    if (filters.dateRange) {
      const startDate = filters.dateRange.start.toISOString()
      const endDate = filters.dateRange.end.toISOString()
      countQuery = countQuery.gte('jobber_created_at', startDate).lte('jobber_created_at', endDate)
      query = query.gte('jobber_created_at', startDate).lte('jobber_created_at', endDate)
    }
    
    // Apply time frame filter
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        default:
          startDate = new Date(0)
      }
      
      const startDateISO = startDate.toISOString()
      countQuery = countQuery.gte('jobber_created_at', startDateISO)
      query = query.gte('jobber_created_at', startDateISO)
    }
    
    // Apply city filter
    if (filters.city) {
      countQuery = countQuery.ilike('service_city', `%${filters.city}%`)
      query = query.ilike('service_city', `%${filters.city}%`)
    }
    
    // Apply source filter
    if (filters.source) {
      countQuery = countQuery.ilike('source', `%${filters.source}%`)
      query = query.ilike('source', `%${filters.source}%`)
    }
    
    // Apply pagination
    if (pagination) {
      const from = (pagination.page - 1) * pagination.pageSize
      const to = from + pagination.pageSize - 1
      query = query.range(from, to)
    }
    
    // Get total count
    const { count, error: countError } = await countQuery
    if (countError) throw countError
    
    // Get data
    const { data, error } = await query
    if (error) throw error
    
    console.log(`Real Jobber requests received: ${data?.length} records of ${count} total`)
    return { data: data || [], total: count || 0 }
  } catch (error) {
    console.error('Error fetching Jobber requests:', error)
    throw error
  }
}

export async function fetchTodayRequestsByCity(city: string): Promise<JobberCityStatusBreakdown> {
  try {
    const end = new Date()
    end.setHours(23, 59, 59, 999)

    // Start from 7 days ago
    const start = new Date()
    start.setDate(start.getDate() - 7)
    start.setHours(0, 0, 0, 0)

    // Map city name to franchise code
    const franchiseCode = city === 'Hamilton' ? 'hamilton' : city === 'North York' ? 'northyork' : city.toLowerCase().replace(' ', '')

    console.log(`[WeekRequests ${city}] Query: ${start.toISOString()} to ${end.toISOString()}, franchise: ${franchiseCode}`)

    const { data, error } = await supabase
      .from('requests')
      .select('id,status,service_city,service_street,jobber_created_at,franchise')
      .gte('jobber_created_at', start.toISOString())
      .lte('jobber_created_at', end.toISOString())
      .eq('franchise', franchiseCode)

    if (error) {
      console.error(`[WeekRequests ${city}] Error:`, error)
      throw error
    }

    console.log(`[WeekRequests ${city}] Found ${data?.length || 0} requests for franchise "${franchiseCode}"`)

    const byStatus: Record<string, number> = {}
    for (const row of data || []) {
      const key = row.status || 'unknown'
      byStatus[key] = (byStatus[key] || 0) + 1
    }

    return {
      total: data?.length || 0,
      byStatus
    }
  } catch (error) {
    console.error('Error fetching today requests by city:', error)
    throw error
  }
}
